/**
 * Vendors Management Hook
 * 
 * Provides comprehensive vendor profile management, including CRUD operations,
 * search functionality, and category-based filtering. This hook is primarily
 * used by clients and admins for vendor discovery and management.
 * 
 * **Vendor Profile Data:**
 * - Business information (name, type, description)
 * - Contact details (address, phone, website)
 * - Service categories and capabilities
 * - Verification status and level
 * - Performance metrics (rating, completion rate, jobs completed)
 * - Financial info (revenue range)
 * - Geographic data (location, lat/long)
 * 
 * **Verification Levels:**
 * - `basic`: Email verified, minimal checks
 * - `verified`: Business verified, documents checked
 * - `premium`: Enhanced verification, background checks
 * 
 * **Key Features:**
 * - Fetch all vendors (auto-loads on mount)
 * - Create new vendor profiles (admin)
 * - Update vendor information
 * - Delete vendor profiles (admin)
 * - Search vendors by name, description, or categories
 * - Filter vendors by service category
 * 
 * **Search Functionality:**
 * - Full-text search across name and description
 * - Service category array search
 * - Case-insensitive matching
 * - Returns matching vendors instantly
 * 
 * **Use Cases:**
 * - Admin vendor management interface
 * - Client vendor discovery and search
 * - Vendor directory/marketplace
 * - RFQ vendor invitation selection
 * - Performance tracking and analytics
 * 
 * @returns {Object} Vendors management
 * @returns {VendorWithProfile[]} vendors - Array of vendor profiles
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} createVendor - Create new vendor profile
 * @returns {Function} updateVendor - Update existing vendor
 * @returns {Function} deleteVendor - Delete vendor profile
 * @returns {Function} searchVendors - Search vendors by keyword
 * @returns {Function} getVendorsByCategory - Filter by service category
 * 
 * @example
 * // Load all vendors
 * const { vendors, loading } = useVendors();
 * 
 * vendors.map(vendor => (
 *   <VendorCard
 *     key={vendor.id}
 *     name={vendor.business_name}
 *     rating={vendor.average_rating}
 *     verified={vendor.is_verified}
 *   />
 * ));
 * 
 * @example
 * // Search vendors
 * const { searchVendors } = useVendors();
 * 
 * const results = await searchVendors('manufacturing');
 * console.log(`Found ${results.length} manufacturers`);
 * 
 * @example
 * // Filter by category
 * const { getVendorsByCategory } = useVendors();
 * 
 * const electricians = await getVendorsByCategory('Electrical');
 * 
 * @example
 * // Update vendor profile
 * const { updateVendor } = useVendors();
 * 
 * await updateVendor(vendorId, {
 *   business_description: 'Updated description',
 *   phone_number: '+1-555-0100'
 * });
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface VendorProfile {
  id: string;
  user_id: string;
  organization_id: string;
  business_name: string;
  business_type?: string | null;
  business_description?: string | null;
  service_categories?: any;
  primary_address?: any;
  is_verified: boolean;
  verification_level?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zip_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  phone_number?: string | null;
  website?: string | null;
  average_rating?: number | null;
  total_jobs_completed?: number | null;
  completion_rate?: number | null;
  annual_revenue_range?: string | null;
  company_story?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorWithProfile extends VendorProfile {
  // Optional profile data - can be loaded separately if needed
  organization_name?: string;
  organization?: {
    id: string;
    name: string;
  };
}

export const useVendors = () => {
  const [vendors, setVendors] = useState<VendorWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select(`
          *,
          organization:organizations!vendor_profiles_enhanced_organization_id_fkey(id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Enrich with organization_name field
      const enrichedVendors = (data || []).map(vendor => ({
        ...vendor,
        organization_name: vendor.organization?.name || vendor.business_name
      }));
      
      setVendors(enrichedVendors);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch vendors",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createVendor = async (vendorData: {
    user_id: string;
    organization_id: string;
    business_name: string;  // Required
    business_type?: string;  // Optional
    business_description?: string;
    service_categories?: string[];
    primary_address?: any;
  }) => {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .insert([vendorData])
        .select('*')
        .single();

      if (error) throw error;
      
      setVendors(prev => [data, ...prev]);
      toast({
        title: "Success",
        description: "Vendor created successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to create vendor",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateVendor = async (id: string, updates: Partial<VendorProfile>) => {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .update(updates as any)
        .eq('id', id)
        .select('*')
        .single();

      if (error) throw error;
      
      setVendors(prev => 
        prev.map(vendor => vendor.id === id ? { ...vendor, ...data } : vendor)
      );
      
      toast({
        title: "Success",
        description: "Vendor updated successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update vendor",
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteVendor = async (id: string) => {
    try {
      const { error } = await supabase
        .from('vendor_profiles_enhanced')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setVendors(prev => prev.filter(vendor => vendor.id !== id));
      toast({
        title: "Success",
        description: "Vendor deleted successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to delete vendor",
        variant: "destructive",
      });
      throw err;
    }
  };

  const searchVendors = async (searchTerm: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .or(`business_name.ilike.%${searchTerm}%,business_description.ilike.%${searchTerm}%,service_categories.cs.{${searchTerm}}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVendors(data || []);
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to search vendors",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getVendorsByCategory = async (category: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .contains('service_categories', [category])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVendors(data || []);
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to filter vendors by category",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  return {
    vendors,
    loading,
    error,
    fetchVendors,
    createVendor,
    updateVendor,
    deleteVendor,
    searchVendors,
    getVendorsByCategory,
  };
};